import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import { useNavigate } from 'react-router-dom';
import { preprocessMarkdown } from '../utils/preprocessMarkdown';
import { resolveDocLink } from '../utils/docLinks';
import { ClipboardDocumentIcon, CheckIcon } from '@heroicons/react/24/outline';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark, oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';

/**
 * Copy to clipboard button for code blocks
 */
function CopyButton({ code }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <button
      onClick={handleCopy}
      className="absolute top-2 right-2 p-2 rounded-md bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 transition-all opacity-0 group-hover:opacity-100 focus:opacity-100"
      title={copied ? 'Copied!' : 'Copy to clipboard'}
      aria-label={copied ? 'Copied!' : 'Copy to clipboard'}
    >
      {copied ? (
        <CheckIcon className="h-4 w-4 text-green-600 dark:text-green-400" />
      ) : (
        <ClipboardDocumentIcon className="h-4 w-4 text-gray-600 dark:text-gray-300" />
      )}
    </button>
  );
}

/**
 * Custom sanitize schema to allow admonition classes and download links
 */
const customSanitizeSchema = {
  ...defaultSchema,
  tagNames: [...(defaultSchema.tagNames || []), 'details', 'summary'],
  attributes: {
    ...defaultSchema.attributes,
    a: [
      ...(defaultSchema.attributes?.a || []),
      'download',  // Allow download attribute for file downloads
      'href',
      'target',
      'rel'
    ],
    div: [
      ...(defaultSchema.attributes?.div || []),
      'className',
      'dataType',
      ['className', 'admonition', 'admonition-*']
    ],
    span: [
      ...(defaultSchema.attributes?.span || []),
      'className',
      ['className', 'admonition-icon']
    ],
    details: [
      'className',
      ['className', 'admonition', 'admonition-*']
    ],
    summary: [
      'className',
      ['className', 'admonition-title']
    ]
  }
};

/**
 * MarkdownRenderer component
 * Renders markdown content with Tailwind CSS styling
 * Supports GitHub Flavored Markdown, tables, raw HTML, and mkdocs admonitions
 */
export default function MarkdownRenderer({ content, variant = 'default', docsRouteBase = '/documentation', externalDocsBase = null }) {
  const navigate = useNavigate();

  // Detect dark mode
  const isDarkMode = document.documentElement.classList.contains('dark');

  // Preprocess markdown to handle mkdocs-specific syntax
  const processedContent = preprocessMarkdown(content);

  return (
    <div className="prose prose-lg dark:prose-invert max-w-none">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw, [rehypeSanitize, customSanitizeSchema]]}
        components={{
          // Headings
          h1: ({node, children, ...props}) => (
            <h1
              className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-6 mt-8 pb-2 border-b-2 border-gray-200/70 dark:border-gray-600/70"
              {...props}
            >{children}</h1>
          ),
          h2: ({node, children, ...props}) => {
            // Simple styling for changelog version headers
            if (variant === 'changelog') {
              const text = children;
              // Check if this is a version header (e.g., "v0.6.2 • Feb 3, 2025")
              if (text && text.toString().match(/v?\d+\.\d+\.\d+/)) {
                return (
                  <h2
                    className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6 mt-12 first:mt-8 pb-3 border-b border-gray-200/70 dark:border-gray-600/70"
                    {...props}
                  >{children}</h2>
                );
              }
            }

            return (
              <h2
                className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4 mt-8"
                {...props}
              >{children}</h2>
            );
          },
          h3: ({node, children, ...props}) => {
            // Simple styling for changelog category headers
            if (variant === 'changelog') {
              const text = children;
              if (text) {
                const textStr = text.toString();

                // Check for common changelog categories and apply minimal styling
                const changelogCategories = ['Added', 'Fixed', 'Changed', 'Security', 'Deprecated', 'Removed'];
                const isChangelogCategory = changelogCategories.some(cat => textStr.includes(cat));

                if (isChangelogCategory) {
                  return (
                    <h3
                      className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-3 mt-6"
                      {...props}
                    >{children}</h3>
                  );
                }
              }
            }

            return (
              <h3
                className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-3 mt-6"
                {...props}
              >{children}</h3>
            );
          },
          h4: ({node, children, ...props}) => (
            <h4
              className="text-lg font-semibold text-brand-600 dark:text-brand-400 mb-3 mt-5"
              {...props}
            >{children}</h4>
          ),
          h5: ({node, children, ...props}) => (
            <h5
              className="text-base font-semibold text-gray-700 dark:text-gray-300 mb-2 mt-4"
              {...props}
            >{children}</h5>
          ),
          h6: ({node, children, ...props}) => (
            <h6
              className="text-sm font-semibold text-gray-600 dark:text-gray-400 mb-2 mt-3"
              {...props}
            >{children}</h6>
          ),

          // Paragraphs and text
          p: ({node, ...props}) => (
            <p
              className="text-gray-700 dark:text-gray-300 mb-4 leading-relaxed"
              {...props}
            />
          ),
          strong: ({node, ...props}) => (
            <strong
              className="font-bold text-gray-900 dark:text-gray-100"
              {...props}
            />
          ),
          em: ({node, ...props}) => (
            <em className="italic" {...props} />
          ),

          // Lists
          ul: ({node, ...props}) => (
            <ul
              className="list-disc list-outside ml-6 mb-4 space-y-2 text-gray-700 dark:text-gray-300"
              {...props}
            />
          ),
          ol: ({node, ...props}) => (
            <ol
              className="list-decimal list-outside ml-6 mb-4 space-y-2 text-gray-700 dark:text-gray-300"
              {...props}
            />
          ),
          li: ({node, ...props}) => (
            <li className="leading-relaxed" {...props} />
          ),

          // Links
          a: ({node, children, ...props}) => {
            const linkClass = 'text-brand-600 dark:text-brand-400 hover:text-brand-800 dark:hover:text-brand-300 underline decoration-brand-600/30 dark:decoration-brand-400/30 hover:decoration-brand-800 dark:hover:decoration-brand-300 underline-offset-2 transition-colors';
            const target = resolveDocLink(props.href, { docsRouteBase, externalDocsBase });
            if (target.kind === 'internal') {
              return (
                <a href={target.href} className={`${linkClass} cursor-pointer`} onClick={(e) => { e.preventDefault(); navigate(target.href); }}>{children}</a>
              );
            }
            const isExternal = target.kind === 'external' || /^https?:/i.test(target.href);
            return (
              <a {...props} href={target.href} className={linkClass} target={isExternal ? '_blank' : undefined} rel={isExternal ? 'noopener noreferrer' : undefined}>{children}</a>
            );
          },

          // Code
          code: ({node, inline, className, children, ...props}) => {
            // Force inline detection by checking if parent is a paragraph
            const isInline = inline !== false && !className?.includes('language-');

            if (isInline) {
              return (
                <code
                  className="not-prose inline-block bg-gray-100 dark:bg-gray-870 text-brand-600 dark:text-brand-400 px-1.5 py-0.5 rounded text-sm font-mono border border-gray-200/70 dark:border-gray-600/70 whitespace-nowrap"
                  style={{ display: 'inline-block' }}
                  {...props}
                >
                  {children}
                </code>
              );
            }

            // For block code, return null - let the pre component handle rendering
            return null;
          },
          pre: ({node, children, ...props}) => {
            // Extract code content and language
            const codeNode = node?.children?.[0];
            if (!codeNode) {
              return <pre className="relative mb-4 rounded-lg overflow-hidden group" {...props}>{children}</pre>;
            }

            // Extract code content from AST
            let code = '';
            if (codeNode?.children) {
              code = codeNode.children
                .map(child => child.value || '')
                .join('');
            }

            // Extract language from className (e.g., "language-python" -> "python")
            const className = codeNode?.properties?.className?.[0] || '';
            const language = className.replace('language-', '') || 'text';

            return (
              <div className="relative mb-4 rounded-lg overflow-hidden group">
                {code && <CopyButton code={code} />}
                <SyntaxHighlighter
                  language={language}
                  style={isDarkMode ? oneDark : oneLight}
                  customStyle={{
                    margin: 0,
                    borderRadius: '0.5rem',
                    padding: '1rem',
                    paddingRight: '3rem', // Extra padding for copy button
                    fontSize: '0.875rem',
                    lineHeight: '1.5',
                  }}
                  // Force our bundled mono so fenced code matches the rest of
                  // the UI instead of the Prism theme's own per-OS mono stack.
                  codeTagProps={{
                    style: {
                      fontFamily:
                        "'JetBrains Mono Variable', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
                    },
                  }}
                  showLineNumbers={false}
                  wrapLines={true}
                  wrapLongLines={true}
                >
                  {code}
                </SyntaxHighlighter>
              </div>
            );
          },

          // Blockquotes
          blockquote: ({node, ...props}) => (
            <blockquote
              className="border-l-4 border-brand-500 dark:border-brand-400 pl-4 py-2 italic text-gray-700 dark:text-gray-300 bg-brand-50 dark:bg-brand-900/20 mb-4 rounded-r"
              {...props}
            />
          ),

          // Horizontal rule
          hr: ({node, ...props}) => (
            <hr
              className="my-8 border-gray-300 dark:border-gray-700"
              {...props}
            />
          ),

          // Tables
          table: ({node, ...props}) => (
            <div className="overflow-x-auto mb-6 rounded-lg border border-gray-200/70 dark:border-gray-600/70">
              <table
                className="min-w-full divide-y divide-gray-100 dark:divide-gray-700"
                {...props}
              />
            </div>
          ),
          thead: ({node, ...props}) => (
            <thead
              className="bg-gray-50 dark:bg-gray-800"
              {...props}
            />
          ),
          tbody: ({node, ...props}) => (
            <tbody
              className="bg-surface dark:bg-gray-870 divide-y divide-gray-100 dark:divide-gray-700"
              {...props}
            />
          ),
          tr: ({node, ...props}) => (
            <tr
              className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
              {...props}
            />
          ),
          th: ({node, ...props}) => (
            <th
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
              {...props}
            />
          ),
          td: ({node, ...props}) => (
            <td
              className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300"
              {...props}
            />
          ),

          // Images. /api/* paths are same-origin in every environment, so the
          // browser resolves them relative to the current page — no need to
          // prepend an explicit origin.
          img: ({node, alt, src, ...props}) => (
            <img
              src={src}
              alt={alt || ''}
              className="rounded-lg shadow-md mb-4 max-w-full h-auto"
              {...props}
            />
          ),

          // Admonitions (custom divs from preprocessor)
          div: ({node, className, ...props}) => {
            if (className?.includes('admonition')) {
              const type = className.match(/admonition-(\w+)/)?.[1] || 'note';

              const typeStyles = {
                warning: 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-400 dark:border-yellow-600',
                danger: 'bg-red-50 dark:bg-red-900/20 border-red-400 dark:border-red-600',
                error: 'bg-red-50 dark:bg-red-900/20 border-red-400 dark:border-red-600',
                tip: 'bg-green-50 dark:bg-green-900/20 border-green-400 dark:border-green-600',
                success: 'bg-green-50 dark:bg-green-900/20 border-green-400 dark:border-green-600',
                important: 'bg-brand-50 dark:bg-brand-900/20 border-brand-400 dark:border-brand-600',
                info: 'bg-blue-50 dark:bg-blue-900/20 border-blue-400 dark:border-blue-600',
                note: 'bg-blue-50 dark:bg-blue-900/20 border-blue-400 dark:border-blue-600',
              };

              const titleStyles = {
                warning: 'text-yellow-800 dark:text-yellow-300',
                danger: 'text-red-800 dark:text-red-300',
                error: 'text-red-800 dark:text-red-300',
                tip: 'text-green-800 dark:text-green-300',
                success: 'text-green-800 dark:text-green-300',
                important: 'text-brand-800 dark:text-brand-300',
                info: 'text-blue-800 dark:text-blue-300',
                note: 'text-blue-800 dark:text-blue-300',
              };

              const contentStyles = {
                warning: 'text-yellow-900 dark:text-yellow-200',
                danger: 'text-red-900 dark:text-red-200',
                error: 'text-red-900 dark:text-red-200',
                tip: 'text-green-900 dark:text-green-200',
                success: 'text-green-900 dark:text-green-200',
                important: 'text-brand-900 dark:text-brand-200',
                info: 'text-blue-900 dark:text-blue-200',
                note: 'text-blue-900 dark:text-blue-200',
              };

              if (className.includes('admonition-title')) {
                return (
                  <div
                    className={`font-semibold mb-2 flex items-center gap-2 ${titleStyles[type] || titleStyles.note}`}
                    {...props}
                  />
                );
              }

              if (className.includes('admonition-content')) {
                return (
                  <div
                    className={`text-sm ${contentStyles[type] || contentStyles.note}`}
                    {...props}
                  />
                );
              }

              return (
                <div
                  className={`my-4 p-4 border-l-4 rounded-r-lg ${typeStyles[type] || typeStyles.note}`}
                  {...props}
                />
              );
            }

            return <div className={className} {...props} />;
          },

          // Spans (for admonition icons)
          span: ({node, className, ...props}) => {
            if (className?.includes('admonition-icon')) {
              return (
                <span className="text-lg" {...props} />
              );
            }
            return <span className={className} {...props} />;
          },

          // Details (collapsible admonitions)
          details: ({node, className, ...props}) => {
            if (className?.includes('admonition')) {
              const type = className.match(/admonition-(\w+)/)?.[1] || 'note';

              const typeStyles = {
                warning: 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-400 dark:border-yellow-600',
                danger: 'bg-red-50 dark:bg-red-900/20 border-red-400 dark:border-red-600',
                error: 'bg-red-50 dark:bg-red-900/20 border-red-400 dark:border-red-600',
                tip: 'bg-green-50 dark:bg-green-900/20 border-green-400 dark:border-green-600',
                success: 'bg-green-50 dark:bg-green-900/20 border-green-400 dark:border-green-600',
                important: 'bg-brand-50 dark:bg-brand-900/20 border-brand-400 dark:border-brand-600',
                info: 'bg-blue-50 dark:bg-blue-900/20 border-blue-400 dark:border-blue-600',
                note: 'bg-blue-50 dark:bg-blue-900/20 border-blue-400 dark:border-blue-600',
                example: 'bg-brand-50 dark:bg-brand-900/20 border-brand-400 dark:border-brand-600',
              };

              return (
                <details
                  className={`my-4 p-4 border-l-4 rounded-r-lg ${typeStyles[type] || typeStyles.note}`}
                  {...props}
                />
              );
            }
            return <details className={className} {...props} />;
          },

          // Summary (collapsible admonition title)
          summary: ({node, className, ...props}) => {
            if (className?.includes('admonition-title')) {
              const type = className.match(/admonition-(\w+)/)?.[1] || 'note';

              const titleStyles = {
                warning: 'text-yellow-800 dark:text-yellow-300',
                danger: 'text-red-800 dark:text-red-300',
                error: 'text-red-800 dark:text-red-300',
                tip: 'text-green-800 dark:text-green-300',
                success: 'text-green-800 dark:text-green-300',
                important: 'text-brand-800 dark:text-brand-300',
                info: 'text-blue-800 dark:text-blue-300',
                note: 'text-blue-800 dark:text-blue-300',
                example: 'text-brand-800 dark:text-brand-300',
              };

              return (
                <summary
                  className={`font-semibold mb-2 flex items-center gap-2 cursor-pointer select-none hover:opacity-75 ${titleStyles[type] || titleStyles.note}`}
                  {...props}
                />
              );
            }
            return <summary className={className} {...props} />;
          },
        }}
      >
        {processedContent}
      </ReactMarkdown>
    </div>
  );
}
